<template>
    <VJoyButton
        :data-testid="`share-profile-social-network--${socialNetwork}`"
        :icon="socialNetworkIcon"
        icon-position="left"
        variant="primary"
        size="small"
        :disabled="disabled"
        :data-test-link="socialNetworkLink"
        @click="handleClickOnShare"
    >
        {{ label }}
    </VJoyButton>
</template>

<script setup lang="ts">
    import {computed, type ComputedRef} from 'vue';
    import {storeToRefs} from 'pinia';
    import {useTranslation, useLocale, navigateTo, useProfilePageViewMode} from '#imports';
    import {safelyNavigateTo} from '@malt/utils';
    import {VJoyButton} from '@maltjoy/core-vue';
    import type {JoyIconsId} from '@maltjoy/icons';
    import {useProfileTracking} from '~/composables/useProfileTracking';
    import {useSkillSetStore} from '~/store/skill-set/skillSetStore';

    type ShareProfileSocialNetworkProps = {
        profileLink: string;
        profileAccountId: string;
        socialNetwork: 'linkedin' | 'facebook' | 'email';
        disabled?: boolean;
    };

    const props = defineProps<ShareProfileSocialNetworkProps>();

    const {t} = useTranslation();
    const {locale} = useLocale();
    const {profileTopSkills} = storeToRefs(useSkillSetStore());
    const {trackProfileSocialShareStarted} = useProfileTracking();
    const {isEditionEnabled} = useProfilePageViewMode();

    const socialNetworkIcon: ComputedRef<JoyIconsId> = computed(() => {
        switch (props.socialNetwork) {
            case 'linkedin':
                return 'linkedin' as JoyIconsId;
            case 'facebook':
                return 'facebook' as JoyIconsId;
            case 'email':
                return 'envelope' as JoyIconsId;
            default:
                return 'social-share' as JoyIconsId;
        }
    });

    const label = computed(() => {
        switch (props.socialNetwork) {
            case 'linkedin':
                return 'LinkedIn';
            case 'facebook':
                return 'Facebook';
            case 'email':
                return 'Email';
            default:
                return props.socialNetwork;
        }
    });

    const topSkills = computed(() => {
        const labels = profileTopSkills.value.map((skill) => skill.label).filter(Boolean);
        if (labels.length === 0) return '';
        return new Intl.ListFormat(locale.value, {style: 'long', type: 'conjunction'}).format(labels);
    });
    const sharedMessage = computed(() => {
        if (!topSkills.value) {
            return t('profile.features.share-profile.shared.message.short', {profileLink: props.profileLink});
        }
        return t('profile.features.share-profile.shared.message', {
            topSkills: topSkills.value,
            profileLink: props.profileLink,
        });
    });

    const facebookSharingLink = computed(() => `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(props.profileLink)}`);
    const linkedinSharingLink = computed(
        () =>
            `https://www.linkedin.com/shareArticle?mini=true&url=${encodeURIComponent(props.profileLink)}&text=${encodeURIComponent(sharedMessage.value)}`,
    );
    const emailSharingLink = computed(
        () =>
            `mailto:?subject=${encodeURIComponent(t('profile.features.share-profile.email.subject'))}&body=${encodeURIComponent(sharedMessage.value)}`,
    );

    const socialNetworkLink = computed(() => {
        switch (props.socialNetwork) {
            case 'linkedin':
                return linkedinSharingLink.value;
            case 'facebook':
                return facebookSharingLink.value;
            case 'email':
                return emailSharingLink.value;
            default:
                return undefined;
        }
    });

    function handleClickOnShare() {
        if (props.disabled) return;
        trackProfileSocialShareStarted(props.socialNetwork, props.profileAccountId, isEditionEnabled.value);
        return navigateTo(socialNetworkLink.value, {external: true, open: {target: '_blank'}});
        // return safelyNavigateTo(socialNetworkLink.value, {externalToThisHost: true, open: {target: '_blank'}});
    }
</script>
